/**
 * Share a dashboard (screenshots/85; brief §2.13 Share; a bottom sheet on phones, 91): add people or teams from the org
 * directory with Can view / Can edit; who has access; Link access (Only people added · Anyone in your organization · Anyone
 * with the link · view only), with expiry, Snapshot / Live data, Update, Reset link and the privacy note; the weekly PDF.
 * Every change is saved at once (PUT /api/dashboards/<id>/share), so the link exists as soon as it's shown; Done closes.
 * Members see it all read-only. Public links are for owners and admins (`canMakePublic`). From the handoff starter's
 * src/analytics/ShareDialog.tsx.
 */
import { useRef, useState } from "react";
import { ChevronDown, Copy, Globe, Link2, ShieldCheck, UserPlus, UserRound, Users, X } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { toastStore } from "../stores/toastStore";
import { canMakePublic, canShare, type Access, type Dashboard, type Principal, type Viewer } from "./dashboards";
import { analyticsStore, useAnalytics, type ShareBody } from "./analyticsStore";

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" }) : "");
const daysLeft = (iso?: string) => (iso ? Math.max(1, Math.round((Date.parse(iso) - Date.now()) / 864e5)) : null);
const expiryKey = (iso?: string): string => { const n = daysLeft(iso); return n === null ? "never" : n <= 7 ? "7" : n <= 30 ? "30" : "90"; };

function Pill<T extends string>({ value, options, onChange, label, icon, disabled }: { value: T; options: { id: T; label: string; disabled?: boolean }[];
  onChange: (v: T) => void; label: string; icon?: React.ReactNode; disabled?: boolean }) {
  return (
    <Dropdown className={`an-pill${icon ? " has-icon" : ""}`} label={label} value={value} disabled={disabled} onChange={onChange} icon={icon} align="end"
      chevron={<ChevronDown size={14} aria-hidden />} options={options.map((o) => ({ value: o.id, label: o.label, disabled: o.disabled }))} />
  );
}
const ACCESS = [{ id: "view" as Access, label: "Can view" }, { id: "edit" as Access, label: "Can edit" }];

export function ShareDialog({ dashboard, viewer, onClose }: { dashboard: Dashboard; viewer: Viewer; onClose: () => void }) {
  const d = useAnalytics((s) => s.dashboards.find((x) => x.id === dashboard.id)) ?? dashboard;
  const directory = useAnalytics((s) => s.snapshot?.directory ?? []);
  const [query, setQuery] = useState("");
  const [pick, setPick] = useState<Principal | null>(null);
  const [inviteAccess, setInviteAccess] = useState<Access>("view");
  const [recipients, setRecipients] = useState(false);
  const [copied, setCopied] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const allowed = canShare(d, viewer), publicOk = canMakePublic(d, viewer);
  const save = (body: ShareBody) => void analyticsStore.getState().share(d.id, body).catch((e: Error) => toastStore.getState().show({ message: e.message || "Something went wrong" }));
  const sub = (id: string) => directory.find((p) => p.id === id)?.sub;
  const matches = query.trim() && !pick ? directory.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()) && p.id !== d.ownerId
    && !d.members.some((m) => m.principal.id === p.id)).slice(0, 5) : [];
  const invite = () => {
    const p = pick ?? matches[0];
    if (!p) return;
    save({ members: [...d.members, { principal: p, access: inviteAccess }] });
    setQuery(""); setPick(null);
  };
  const url = d.link.mode === "public" && d.link.token ? `${location.origin}/d/${d.link.token}` : d.link.mode === "org" ? `${location.origin}/dashboard/analytics/${d.id}` : "";
  const team = directory.find((p) => p.kind === "team" && p.id === d.orgId);
  const to = d.schedule?.to ?? [];
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); }
    catch { toastStore.getState().show({ message: `Copy this link: ${url}` }); }
  };

  return (
    <div className="an-scrim an-scrim--sheet" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className="an-modal an-share" role="dialog" aria-modal="true" aria-labelledby="an-share-title">
        <header className="an-modal__head an-modal__head--plain">
          <h2 id="an-share-title">Share “{d.title}”</h2>
          <button className="an-icon-btn" aria-label="Close" onClick={onClose}><X size={18} aria-hidden /></button>
        </header>
        <fieldset className="an-modal__body an-share__body" disabled={!allowed}>
          {!allowed && <p className="an-help">Only the owner and admins can change sharing</p>}
          <div className="an-share__invite">
            <label className="an-input has-icon">
              <UserPlus size={15} aria-hidden />
              <input placeholder="Add people or teams" aria-label="Add people or teams" value={query} role="combobox" aria-expanded={matches.length > 0} aria-controls="an-share-matches"
                onChange={(e) => { setQuery(e.target.value); setPick(null); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); invite(); } }} />
            </label>
            <Pill value={inviteAccess} options={ACCESS} onChange={setInviteAccess} label="Access for new people" />
            <button className="btn btn--secondary" onClick={invite} disabled={!pick && !matches.length}>Invite</button>
            {matches.length > 0 && (
              <ul id="an-share-matches" className="widget-menu ov-menu an-menu an-share__matches" role="listbox" aria-label="People and teams">
                {matches.map((p) => (
                  <li key={p.id} role="option" aria-selected={false} tabIndex={0} onClick={() => { setPick(p); setQuery(p.name); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { setPick(p); setQuery(p.name); } }}>
                    {p.kind === "team" ? <Users size={15} aria-hidden /> : <UserRound size={15} aria-hidden />}<span>{p.name}</span><small>{p.sub}</small>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <h3 className="eyebrow">Who has access</h3>
          <ul className="an-people">
            <li><span className="an-avatar"><UserRound size={16} aria-hidden /></span>
              <span className="an-people__name"><strong>{d.ownerId === viewer.userId ? "You" : d.ownerName ?? "Owner"}</strong><small>Owner</small></span>
              <span className="an-people__role">Owner</span></li>
            {d.visibility === "team" && (
              <li><span className="an-avatar"><Users size={16} aria-hidden /></span>
                <span className="an-people__name"><strong>{team?.name ?? "Your team"}</strong>{team?.sub && <small>{team.sub}</small>}</span>
                <Pill value={d.teamAccess} options={ACCESS} onChange={(teamAccess) => save({ teamAccess })} label="Team access" /></li>
            )}
            {d.members.map((m) => (
              <li key={m.principal.id}><span className="an-avatar">{m.principal.kind === "team" ? <Users size={16} aria-hidden /> : <UserRound size={16} aria-hidden />}</span>
                <span className="an-people__name"><strong>{m.principal.name}</strong>{sub(m.principal.id) && <small>{sub(m.principal.id)}</small>}</span>
                <Pill value={m.access} options={ACCESS} label={`${m.principal.name} access`}
                  onChange={(access) => save({ members: d.members.map((x) => (x.principal.id === m.principal.id ? { ...x, access } : x)) })} />
                <button className="an-icon-btn an-people__remove" aria-label={`Remove ${m.principal.name}`} onClick={() => save({ members: d.members.filter((x) => x.principal.id !== m.principal.id) })}><X size={14} aria-hidden /></button>
              </li>
            ))}
          </ul>
          <section className="an-link">
            <div className="an-link__head">
              <Link2 size={16} aria-hidden /><strong id="an-link-label">Link access</strong>
              <Pill value={d.link.mode} label="Link access" icon={<Globe size={15} aria-hidden />} onChange={(mode) => save({ link: { mode } })} options={[
                { id: "off", label: "Only people added" }, { id: "org", label: "Anyone in your organization" },
                { id: "public", label: "Anyone with the link · view only", disabled: !publicOk && d.link.mode !== "public" }]} />
            </div>
            {d.link.mode !== "off" && <>
              <div className="an-link__url">
                <label className="an-input has-icon"><Link2 size={15} aria-hidden /><input readOnly value={url.replace(/^https?:\/\//, "")} aria-label="Dashboard link" onFocus={(e) => e.target.select()} /></label>
                <button className="btn btn--secondary" onClick={() => void copy()}><Copy size={15} aria-hidden />{copied ? "Copied" : "Copy link"}</button>
              </div>
              {d.link.mode === "public" && <>
                <div className="an-link__row">
                  <span>Expires</span>
                  <Pill value={expiryKey(d.link.expiresAt)} label="Expires" onChange={(v) => save({ link: { mode: "public", expiresDays: v === "never" ? null : (Number(v) as 7 | 30 | 90) } })}
                    options={[{ id: "7", label: "In 7 days" }, { id: "30", label: "In 30 days" }, { id: "90", label: "In 90 days" }, { id: "never", label: "Never" }]} />
                  <button className="an-textlink an-link__reset" onClick={() => save({ link: { mode: "public", reset: true } })}>Reset link</button>
                </div>
                <div className="an-link__row">
                  <span>Link shows</span>
                  <div className="view-switch" role="radiogroup" aria-label="Link shows">
                    {(["snapshot", "live"] as const).map((v) => <button key={v} role="radio" aria-checked={d.link.data === v} onClick={() => save({ link: { mode: "public", data: v } })}>{v === "snapshot" ? "Snapshot" : "Live data"}</button>)}
                  </div>
                  {d.link.data === "snapshot" && d.link.publishedAt && (
                    <span className="an-link__pub">Published {when(d.link.publishedAt)} · <button className="an-textlink an-textlink--muted" onClick={() => save({ link: { mode: "public", update: true } })}>Update</button></span>
                  )}
                </div>
              </>}
              <p className="an-link__note"><ShieldCheck size={15} aria-hidden />Link viewers never see team-only widgets. Meeting log and Ward responses are left out, and nobody’s name from How would you vote? is ever shown.</p>
            </>}
          </section>
          <div className="an-sched">
            <button type="button" className="switch switch--md" role="switch" aria-checked={!!d.schedule} aria-labelledby="an-sched-label" onClick={() => save({ schedule: d.schedule ? { on: false } : { on: true } })} />
            <span className="an-sched__text"><strong id="an-sched-label">Email a PDF every Monday at 8:00 AM</strong>
              {d.schedule && <small>To {to.map((p) => p.name).join(", ") || team?.name} · uses the print layout</small>}</span>
            {d.schedule && <button className="an-textlink" aria-expanded={recipients} onClick={() => setRecipients((v) => !v)}>Edit</button>}
          </div>
          {d.schedule && recipients && (
            <ul className="an-recipients" aria-label="Recipients">
              {directory.map((p) => {
                const on = to.some((x) => x.id === p.id);
                return (
                  <li key={p.id}><label><input type="checkbox" checked={on} onChange={() => save({ schedule: { on: true, to: on ? to.filter((x) => x.id !== p.id) : [...to, p] } })} />{p.name}<small>{p.sub}</small></label></li>
                );
              })}
            </ul>
          )}
        </fieldset>
        <footer className="an-modal__foot">
          <button className="btn btn--primary" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
